import React, { useEffect, useMemo, useState } from 'react';
import { doc, onSnapshot, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';
import ConfirmModal from '../../components/ConfirmModal';
import { getAllBookableLeafItems } from '../../utils/offerableItems';
import './ManageOffers.css';

const OFFER_TYPE_ALL = 'all_services';
const OFFER_TYPE_SPECIFIC = 'specific_services';
const COMPACT_LIST_LIMIT = 5;

function filterLeafItems(list, query) {
  const q = query.trim().toLowerCase();
  if (!q) return list;
  return list.filter(
    (item) =>
      item.displayName.toLowerCase().includes(q) || (item.categoryName || '').toLowerCase().includes(q)
  );
}

// Firestore doesn't guarantee it returns map fields in the order they were
// written, so plain JSON.stringify can report a diff where there isn't one.
// Sorting keys recursively before stringifying makes the comparison order-independent.
function stableStringify(value) {
  if (Array.isArray(value)) return `[${value.map(stableStringify).join(',')}]`;
  if (value && typeof value === 'object') {
    return `{${Object.keys(value)
      .sort()
      .map((key) => `${JSON.stringify(key)}:${stableStringify(value[key])}`)
      .join(',')}}`;
  }
  return JSON.stringify(value);
}

function ManageOffers() {
  const { salonData } = useAuth();
  const salonId = salonData?.salonId;

  const [salonDoc, setSalonDoc] = useState(null);
  const [docLoading, setDocLoading] = useState(true);

  useEffect(() => {
    if (!salonId) return undefined;
    const unsub = onSnapshot(
      doc(db, 'salons', salonId),
      (snap) => {
        setSalonDoc(snap.exists() ? snap.data() : null);
        setDocLoading(false);
      },
      () => setDocLoading(false)
    );
    return () => unsub();
  }, [salonId]);

  const existingOffer = salonDoc?.profile?.servicesData?.serviceOffer || null;

  const [enabled, setEnabled] = useState(false);
  const [offerName, setOfferName] = useState('');
  const [offerType, setOfferType] = useState(OFFER_TYPE_ALL);
  const [allDiscount, setAllDiscount] = useState('');
  const [allStartDate, setAllStartDate] = useState('');
  const [allEndDate, setAllEndDate] = useState('');
  const [items, setItems] = useState({});
  const [initialized, setInitialized] = useState(false);

  // Seed local form state once from Firestore — not on every live snapshot
  // tick, so it doesn't clobber in-progress edits while the salon is typing.
  useEffect(() => {
    if (initialized || docLoading) return;
    if (existingOffer) {
      setEnabled(existingOffer.enabled === true);
      setOfferName(existingOffer.name || '');
      setOfferType(existingOffer.type || OFFER_TYPE_ALL);
      setAllDiscount(
        existingOffer.allServices?.discountPercent != null
          ? String(existingOffer.allServices.discountPercent)
          : ''
      );
      setAllStartDate(existingOffer.allServices?.startDate || '');
      setAllEndDate(existingOffer.allServices?.endDate || '');
      setItems(existingOffer.items || {});
    }
    setInitialized(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialized, docLoading]);

  const [searchQuery, setSearchQuery] = useState('');
  const [showSelector, setShowSelector] = useState(false);
  const [selectorSearch, setSelectorSearch] = useState('');
  const [defaultDiscount, setDefaultDiscount] = useState('');
  const [defaultStartDate, setDefaultStartDate] = useState('');
  const [defaultEndDate, setDefaultEndDate] = useState('');
  const [editingItemKey, setEditingItemKey] = useState(null);
  const [editDiscount, setEditDiscount] = useState('');
  const [editStartDate, setEditStartDate] = useState('');
  const [editEndDate, setEditEndDate] = useState('');
  const [publishConfirmOpen, setPublishConfirmOpen] = useState(false);
  const [resetConfirmOpen, setResetConfirmOpen] = useState(false);
  const [toggleOffConfirmOpen, setToggleOffConfirmOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const leafItems = useMemo(
    () => getAllBookableLeafItems(salonDoc?.profile?.servicesData),
    [salonDoc]
  );
  const inlineFilteredItems = useMemo(() => filterLeafItems(leafItems, searchQuery), [leafItems, searchQuery]);
  const modalFilteredItems = useMemo(
    () => filterLeafItems(leafItems, selectorSearch),
    [leafItems, selectorSearch]
  );

  const groupedModalItems = useMemo(() => {
    const groups = {};
    const comboItems = [];
    modalFilteredItems.forEach((item) => {
      if (item.isCombo) {
        comboItems.push(item);
        return;
      }
      const key = item.categoryName || 'Services';
      if (!groups[key]) groups[key] = [];
      groups[key].push(item);
    });
    const result = Object.keys(groups).map((name) => ({ name, data: groups[name] }));
    if (comboItems.length) result.push({ name: 'Combos', data: comboItems });
    return result;
  }, [modalFilteredItems]);

  const selectedItems = useMemo(() => Object.values(items), [items]);

  const toggleItem = (item) => {
    setItems((prev) => {
      const next = { ...prev };
      if (next[item.itemKey]) {
        delete next[item.itemKey];
      } else {
        // Newly selected items inherit the default %/dates (if already set)
        // so the salon doesn't have to re-enter them for every service —
        // they can still override a specific one via its Edit button.
        const seedDiscount = Math.min(100, Math.max(0, parseInt(defaultDiscount, 10) || 0));
        const hasDefault = seedDiscount > 0 && !!defaultStartDate && !!defaultEndDate;
        next[item.itemKey] = {
          itemKey: item.itemKey,
          categoryName: item.categoryName,
          serviceName: item.serviceName,
          subServiceName: item.subServiceName,
          genderType: item.genderType,
          comboTitle: item.comboTitle,
          isCombo: item.isCombo,
          discountPercent: hasDefault ? seedDiscount : null,
          startDate: hasDefault ? defaultStartDate : null,
          endDate: hasDefault ? defaultEndDate : null,
          complete: hasDefault
        };
      }
      return next;
    });
  };

  const applyDefaultToSelected = () => {
    const discount = Math.min(100, Math.max(0, parseInt(defaultDiscount, 10) || 0));
    if (!discount) {
      alert('Please enter a discount percentage greater than 0.');
      return;
    }
    if (!defaultStartDate || !defaultEndDate) {
      alert('Please select a start and end date.');
      return;
    }
    if (new Date(defaultEndDate) < new Date(defaultStartDate)) {
      alert('End date must be after start date.');
      return;
    }
    setItems((prev) => {
      const next = {};
      Object.keys(prev).forEach((key) => {
        next[key] = {
          ...prev[key],
          discountPercent: discount,
          startDate: defaultStartDate,
          endDate: defaultEndDate,
          complete: true
        };
      });
      return next;
    });
  };

  const openItemEditor = (itemKey) => {
    const item = items[itemKey];
    if (!item) return;
    setEditingItemKey(itemKey);
    setEditDiscount(item.discountPercent ? String(item.discountPercent) : '');
    setEditStartDate(item.startDate || '');
    setEditEndDate(item.endDate || '');
  };

  const removeSelectedItem = (itemKey) => {
    setItems((prev) => {
      const next = { ...prev };
      delete next[itemKey];
      return next;
    });
  };

  const saveItemEdit = () => {
    const discount = Math.min(100, Math.max(0, parseInt(editDiscount, 10) || 0));
    if (!discount) {
      alert('Please enter a discount percentage greater than 0.');
      return;
    }
    if (!editStartDate || !editEndDate) {
      alert('Please select a start and end date.');
      return;
    }
    if (new Date(editEndDate) < new Date(editStartDate)) {
      alert('End date must be after start date.');
      return;
    }
    setItems((prev) => ({
      ...prev,
      [editingItemKey]: {
        ...prev[editingItemKey],
        discountPercent: discount,
        startDate: editStartDate,
        endDate: editEndDate,
        complete: true
      }
    }));
    setEditingItemKey(null);
  };

  const buildOfferPayload = () => {
    if (!enabled) return { enabled: false };
    const base = {
      enabled: true,
      name: offerName.trim(),
      type: offerType,
      updatedAt: new Date().toISOString()
    };
    if (offerType === OFFER_TYPE_ALL) {
      return {
        ...base,
        allServices: {
          discountPercent: Math.min(100, Math.max(0, parseInt(allDiscount, 10) || 0)),
          startDate: allStartDate,
          endDate: allEndDate
        }
      };
    }
    return { ...base, items };
  };

  // Same shape as buildOfferPayload but without updatedAt, so it can be
  // diffed against the live offer to tell whether there's anything new to publish.
  const currentComparablePayload = useMemo(() => {
    if (!enabled) return { enabled: false };
    const base = { enabled: true, name: offerName.trim(), type: offerType };
    if (offerType === OFFER_TYPE_ALL) {
      return {
        ...base,
        allServices: {
          discountPercent: Math.min(100, Math.max(0, parseInt(allDiscount, 10) || 0)),
          startDate: allStartDate,
          endDate: allEndDate
        }
      };
    }
    return { ...base, items };
  }, [enabled, offerName, offerType, allDiscount, allStartDate, allEndDate, items]);

  const liveComparablePayload = useMemo(() => {
    if (!existingOffer?.enabled) return { enabled: false };
    const { updatedAt, ...rest } = existingOffer;
    return rest;
  }, [existingOffer]);

  const isOfferLive = existingOffer?.enabled === true;
  const hasUnpublishedChanges =
    stableStringify(currentComparablePayload) !== stableStringify(liveComparablePayload);
  const publishDisabled = saving || (isOfferLive && !hasUnpublishedChanges);

  const validateBeforePublish = () => {
    if (!enabled) return true;
    if (!offerName.trim()) {
      alert('Please give this offer a name.');
      return false;
    }
    if (offerType === OFFER_TYPE_ALL) {
      const discount = parseInt(allDiscount, 10) || 0;
      if (discount <= 0) {
        alert('Please enter a discount percentage greater than 0.');
        return false;
      }
      if (!allStartDate || !allEndDate) {
        alert('Please select a start and end date.');
        return false;
      }
      if (new Date(allEndDate) < new Date(allStartDate)) {
        alert('End date must be after start date.');
        return false;
      }
    } else {
      if (selectedItems.length === 0) {
        alert('Please select at least one service for this offer.');
        return false;
      }
      if (selectedItems.some((it) => !it.complete)) {
        alert('Please set a discount % and dates for every selected service before publishing.');
        return false;
      }
    }
    return true;
  };

  const persistOffer = async (payload) => {
    setSaving(true);
    try {
      const existing = salonDoc?.profile?.servicesData || {};
      const { offerSettings, ...rest } = existing;
      await updateDoc(doc(db, 'salons', salonId), {
        'profile.servicesData': { ...rest, serviceOffer: payload },
        updatedAt: serverTimestamp()
      });
      return true;
    } catch (err) {
      alert(`Failed to save offer: ${err.message}`);
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handlePublishClick = () => {
    if (!validateBeforePublish()) return;
    setPublishConfirmOpen(true);
  };

  const doPublish = async () => {
    setPublishConfirmOpen(false);
    const ok = await persistOffer(buildOfferPayload());
    if (ok) alert('Your offer has been published and is now visible to customers.');
  };

  const clearOfferFields = () => {
    setEnabled(false);
    setOfferName('');
    setOfferType(OFFER_TYPE_ALL);
    setAllDiscount('');
    setAllStartDate('');
    setAllEndDate('');
    setItems({});
    setDefaultDiscount('');
    setDefaultStartDate('');
    setDefaultEndDate('');
  };

  const doReset = async () => {
    setResetConfirmOpen(false);
    const ok = await persistOffer({ enabled: false });
    if (ok) {
      clearOfferFields();
      alert('Your offer has been cleared.');
    }
  };

  const doToggleOff = async () => {
    setToggleOffConfirmOpen(false);
    const ok = await persistOffer({ enabled: false });
    if (ok) clearOfferFields();
  };

  const handleToggleChange = (e) => {
    const next = e.target.checked;
    if (!next) {
      setToggleOffConfirmOpen(true);
      return;
    }
    // Turning the offer on always starts from a fresh, blank form.
    clearOfferFields();
    setEnabled(true);
  };

  const renderCheckboxRow = (item) => {
    const checked = !!items[item.itemKey];
    return (
      <label key={item.itemKey} className="offers-item-row">
        <input type="checkbox" checked={checked} onChange={() => toggleItem(item)} />
        <span className="offers-item-name">{item.displayName}</span>
      </label>
    );
  };

  const renderSearchInput = (value, onChangeValue, placeholder) => (
    <div className="offers-search-wrapper">
      <input
        type="text"
        className="offers-input"
        value={value}
        onChange={(e) => onChangeValue(e.target.value)}
        placeholder={placeholder}
      />
      {value.length > 0 && (
        <button
          type="button"
          className="offers-search-clear"
          onClick={() => onChangeValue('')}
          aria-label="Clear search"
        >
          ×
        </button>
      )}
    </div>
  );

  if (!salonId || docLoading) {
    return (
      <div className="offers-page">
        <div className="offers-loading">Loading...</div>
      </div>
    );
  }

  const editingItem = editingItemKey ? items[editingItemKey] : null;

  return (
    <div className="offers-page">
      <div className="offers-header">
        <h2>Manage Offers</h2>
        <p className="offers-subtitle">Set up a discount for all services or specific services</p>
      </div>

      <div className="offers-enable-row">
        <div>
          <div className="offers-enable-title">Enable Offer</div>
          <div className="offers-enable-subtitle">Show discounted prices to customers</div>
        </div>
        <label className="switch">
          <input type="checkbox" checked={enabled} onChange={handleToggleChange} />
          <span className="slider" />
        </label>
      </div>

      {enabled && (
        <div className="offers-form-section">
          <label>Offer Name</label>
          <input
            type="text"
            className="offers-input"
            value={offerName}
            onChange={(e) => setOfferName(e.target.value)}
            placeholder="e.g. Diwali Sale"
          />

          <label>Apply To</label>
          <div className="offers-type-buttons">
            <button
              type="button"
              className={`offers-type-btn ${offerType === OFFER_TYPE_ALL ? 'active' : ''}`}
              onClick={() => setOfferType(OFFER_TYPE_ALL)}
            >
              All Services
            </button>
            <button
              type="button"
              className={`offers-type-btn ${offerType === OFFER_TYPE_SPECIFIC ? 'active' : ''}`}
              onClick={() => setOfferType(OFFER_TYPE_SPECIFIC)}
            >
              Specific Services
            </button>
          </div>

          {offerType === OFFER_TYPE_ALL ? (
            <div className="offers-all-box">
              <label>Discount Percentage</label>
              <div className="offers-discount-input">
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={allDiscount}
                  onChange={(e) => setAllDiscount(e.target.value)}
                  placeholder="10"
                />
                <span>%</span>
              </div>

              <label>Offer Period</label>
              <div className="offers-date-range">
                <div>
                  <label className="offers-date-sublabel">Start Date</label>
                  <input
                    type="date"
                    value={allStartDate}
                    max={allEndDate || undefined}
                    onChange={(e) => setAllStartDate(e.target.value)}
                  />
                </div>
                <span className="offers-date-sep">to</span>
                <div>
                  <label className="offers-date-sublabel">End Date</label>
                  <input
                    type="date"
                    value={allEndDate}
                    min={allStartDate || undefined}
                    onChange={(e) => setAllEndDate(e.target.value)}
                  />
                </div>
              </div>
              <p className="offers-hint">
                This discount applies to every service, sub-service, and combo during the offer period.
              </p>
            </div>
          ) : (
            <div className="offers-specific-box">
              {renderSearchInput(searchQuery, setSearchQuery, '🔍 Search services...')}

              {inlineFilteredItems.slice(0, COMPACT_LIST_LIMIT).map(renderCheckboxRow)}
              {inlineFilteredItems.length === 0 && <p className="offers-hint">No services match your search.</p>}

              <button
                type="button"
                className="offers-more-btn"
                onClick={() => {
                  setSelectorSearch(searchQuery);
                  setShowSelector(true);
                }}
              >
                More ({leafItems.length} total service{leafItems.length === 1 ? '' : 's'})
              </button>

              <div className="offers-default-box">
                <label>Default Offer for Selected Services</label>
                <div className="offers-discount-input">
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={defaultDiscount}
                    onChange={(e) => setDefaultDiscount(e.target.value)}
                    placeholder="10"
                  />
                  <span>%</span>
                </div>
                <div className="offers-date-range">
                  <div>
                    <label className="offers-date-sublabel">Start Date</label>
                    <input
                      type="date"
                      value={defaultStartDate}
                      max={defaultEndDate || undefined}
                      onChange={(e) => setDefaultStartDate(e.target.value)}
                    />
                  </div>
                  <span className="offers-date-sep">to</span>
                  <div>
                    <label className="offers-date-sublabel">End Date</label>
                    <input
                      type="date"
                      value={defaultEndDate}
                      min={defaultStartDate || undefined}
                      onChange={(e) => setDefaultEndDate(e.target.value)}
                    />
                  </div>
                </div>
                <button
                  type="button"
                  className="offers-apply-default-btn"
                  onClick={applyDefaultToSelected}
                  disabled={selectedItems.length === 0}
                >
                  Apply to Selected ({selectedItems.length})
                </button>
                <p className="offers-hint">
                  Applied to every selected service below by default. Use Edit on a service to set a different %
                  or dates just for it.
                </p>
              </div>

              <label className="offers-selected-label">Selected Services ({selectedItems.length})</label>
              {selectedItems.length === 0 ? (
                <p className="offers-hint">No services selected yet.</p>
              ) : (
                selectedItems.map((item) => (
                  <div key={item.itemKey} className="offers-selected-row">
                    <div className="offers-selected-info">
                      <div className="offers-selected-name">
                        {item.isCombo
                          ? item.comboTitle
                          : item.subServiceName
                          ? `${item.serviceName} - ${item.subServiceName}`
                          : item.serviceName}
                      </div>
                      {item.complete ? (
                        <div className="offers-selected-meta">
                          {item.discountPercent}% • {item.startDate} to {item.endDate}
                        </div>
                      ) : (
                        <div className="offers-incomplete">Incomplete — set % and dates</div>
                      )}
                    </div>
                    <div className="offers-selected-actions">
                      <button
                        type="button"
                        className="offers-edit-btn"
                        onClick={() => openItemEditor(item.itemKey)}
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        className="offers-remove-btn"
                        onClick={() => removeSelectedItem(item.itemKey)}
                        aria-label="Remove service"
                      >
                        ×
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      )}

      <div className="offers-actions">
        <button type="button" className="offers-publish-btn" onClick={handlePublishClick} disabled={publishDisabled}>
          {saving ? 'Saving...' : isOfferLive && !hasUnpublishedChanges ? 'Offer Live' : 'Publish Offer'}
        </button>
        <button type="button" className="offers-reset-btn" onClick={() => setResetConfirmOpen(true)} disabled={saving}>
          Reset Offer
        </button>
      </div>

      {/* Full service selector modal */}
      {showSelector && (
        <div className="modal-overlay" onClick={() => setShowSelector(false)}>
          <div className="modal-content offers-selector-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Select Services</h3>
              <button className="modal-close" onClick={() => setShowSelector(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="offers-search-wrapper">
                <input
                  type="text"
                  className="offers-input"
                  value={selectorSearch}
                  onChange={(e) => setSelectorSearch(e.target.value)}
                  placeholder="🔍 Search services..."
                  autoFocus
                />
                {selectorSearch.length > 0 && (
                  <button
                    type="button"
                    className="offers-search-clear"
                    onClick={() => setSelectorSearch('')}
                    aria-label="Clear search"
                  >
                    ×
                  </button>
                )}
              </div>
              <div className="offers-selector-scroll">
                {groupedModalItems.map((group) => (
                  <div key={group.name} className="offers-selector-group">
                    <h4>{group.name}</h4>
                    {group.data.map(renderCheckboxRow)}
                  </div>
                ))}
                {groupedModalItems.length === 0 && <p className="offers-hint">No services match your search.</p>}
              </div>
            </div>
            <div className="modal-footer">
              <button className="save-modal-btn" onClick={() => setShowSelector(false)}>
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Per-item discount/date editor */}
      {editingItemKey && (
        <div className="modal-overlay" onClick={() => setEditingItemKey(null)}>
          <div className="modal-content modal-content-sm" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>
                {editingItem?.isCombo
                  ? editingItem.comboTitle
                  : editingItem?.subServiceName
                  ? `${editingItem.serviceName} - ${editingItem.subServiceName}`
                  : editingItem?.serviceName}
              </h3>
            </div>
            <div className="modal-body">
              <label>Discount Percentage</label>
              <div className="offers-discount-input">
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={editDiscount}
                  onChange={(e) => setEditDiscount(e.target.value)}
                  placeholder="0"
                />
                <span>%</span>
              </div>
              <label>Offer Period</label>
              <div className="offers-date-range">
                <div>
                  <label className="offers-date-sublabel">Start Date</label>
                  <input
                    type="date"
                    value={editStartDate}
                    max={editEndDate || undefined}
                    onChange={(e) => setEditStartDate(e.target.value)}
                  />
                </div>
                <span className="offers-date-sep">to</span>
                <div>
                  <label className="offers-date-sublabel">End Date</label>
                  <input
                    type="date"
                    value={editEndDate}
                    min={editStartDate || undefined}
                    onChange={(e) => setEditEndDate(e.target.value)}
                  />
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="cancel-modal-btn" onClick={() => setEditingItemKey(null)}>
                Cancel
              </button>
              <button className="save-modal-btn" onClick={saveItemEdit}>
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={publishConfirmOpen}
        title="Publish Offer"
        message={enabled ? 'Publish this offer? It will be saved immediately.' : 'Save with offers turned off?'}
        confirmLabel="Publish"
        onConfirm={doPublish}
        onCancel={() => setPublishConfirmOpen(false)}
      />

      <ConfirmModal
        isOpen={resetConfirmOpen}
        title="Reset Offer"
        message="This will remove your current offer completely. Continue?"
        confirmLabel="Reset"
        danger
        onConfirm={doReset}
        onCancel={() => setResetConfirmOpen(false)}
      />

      <ConfirmModal
        isOpen={toggleOffConfirmOpen}
        title="Turn Off Offer"
        message="Do you want to turn off the offer? This will clear all existing offer details and save."
        confirmLabel="Turn Off"
        danger
        onConfirm={doToggleOff}
        onCancel={() => setToggleOffConfirmOpen(false)}
      />
    </div>
  );
}

export default ManageOffers;
